import { useState, useEffect } from 'react';

import Loading from "../../components/Loading";
import Item from './components/Item';
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function List() {
    const [loading, setLoading] = useState(true);
    let [transactions, setTransactions] = useState([]);
    const [memberFilter, setMemberFilter] = useState('');
    const [processing, setProcessing] = useState(false);
    const [processResult, setProcessResult] = useState(null);
    const [billingStatus, setBillingStatus] = useState(null);
    const [showBillingStatus, setShowBillingStatus] = useState(false);
    const [loadingBillingStatus, setLoadingBillingStatus] = useState(false);

    useEffect(async () => {
        await refresh();
    }, []);

    const refresh = async () => {
        setLoading(true);
        const result = await Data.fetchMembershipTransactions();
        setTransactions(result.data);
        setLoading(false);
    }

    const exportCsv = async () => {
        const response = await Data.exportMembershipTransactions();
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'membership-transactions.csv');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    const runRecurring = async () => {
        setProcessing(true);
        setProcessResult(null);
        try {
            const response = await Data.processRecurringPayments();
            const { stats } = response.data;
            setProcessResult({ success: true, stats });
            await refresh();
        } catch (err) {
            setProcessResult({ success: false, error: 'Failed to process recurring payments.' });
        } finally {
            setProcessing(false);
        }
    }

    const checkBillingStatus = async () => {
        if (showBillingStatus) {
            setShowBillingStatus(false);
            return;
        }

        setLoadingBillingStatus(true);
        setShowBillingStatus(true);
        try {
            const response = await Data.fetchBillingStatus();
            setBillingStatus(response.data);
        } catch (err) {
            setBillingStatus({ error: 'Failed to fetch billing status.' });
        } finally {
            setLoadingBillingStatus(false);
        }
    }

    if (loading) return <Loading button={false} />;

    const members = [...new Map(
        transactions
            .filter(t => t.email)
            .map(t => [t.email, { email: t.email, name: `${t.firstName || ''} ${t.lastName || ''}`.trim() }])
    ).values()].sort((a, b) => a.name.localeCompare(b.name));

    const filtered = memberFilter
        ? transactions.filter(t => t.email === memberFilter)
        : transactions;

    const renderProcessResult = () => {
        if (!processResult) return null;
        if (!processResult.success) {
            return <p style={{ color: 'red' }}>{processResult.error}</p>;
        }
        const { stats } = processResult;
        const wp = stats?.worldpay;
        const su = stats?.sumup;
        const totalFound = (wp?.found || 0) + (su?.found || 0);
        const totalSucceeded = (wp?.succeeded || 0) + (su?.succeeded || 0);
        const totalFailed = (wp?.failed || 0) + (su?.failed || 0);

        if (totalFound === 0) {
            return <p style={{ color: 'orange' }}>No subscriptions were due for billing. Check billing status for details.</p>;
        }

        return (
            <div style={{ marginTop: 8 }}>
                <p style={{ color: totalFailed > 0 ? 'orange' : 'green', margin: '4px 0' }}>
                    Found {totalFound} subscription{totalFound !== 1 ? 's' : ''} due — {totalSucceeded} succeeded, {totalFailed} failed.
                </p>
                {wp?.errors?.length > 0 && wp.errors.map((e, i) => (
                    <p key={i} style={{ color: 'red', fontSize: 13, margin: '2px 0' }}>Worldpay error: {e.error}</p>
                ))}
                {su?.errors?.length > 0 && su.errors.map((e, i) => (
                    <p key={i} style={{ color: 'red', fontSize: 13, margin: '2px 0' }}>SumUp error: {e.error}</p>
                ))}
            </div>
        );
    }

    const renderBillingStatus = () => {
        if (!showBillingStatus) return null;
        if (loadingBillingStatus) return <p>Loading billing status...</p>;
        if (billingStatus?.error) return <p style={{ color: 'red' }}>{billingStatus.error}</p>;
        if (!billingStatus?.subscriptions?.length) return <p style={{ color: 'green' }}>No overdue or past-due subscriptions.</p>;

        return (
            <div className={styles.billingStatusCard}>
                <div className={styles.billingStatusHeader}>
                    <div>
                        <h4>Billing Status ({billingStatus.count} subscription{billingStatus.count !== 1 ? 's' : ''})</h4>
                        <p className={styles.billingStatusSub}>Overdue and past-due subscriptions.</p>
                    </div>
                    <button className={`btn ${styles.billingHideButton}`} onClick={() => setShowBillingStatus(false)}>
                        Hide Table
                    </button>
                </div>
                <div className={styles.tableWrap}>
                    <table className={`${styles.table} ${styles.billingStatusTable}`}>
                        <thead>
                            <tr>
                                <th>Member</th>
                                <th>Plan</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Next Billing</th>
                                <th>Issue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingStatus.subscriptions.map(s => (
                                <tr key={s._id}>
                                    <td>{s.user}<br /><small className={styles.billingStatusEmail}>{s.email}</small></td>
                                    <td>{s.plan}</td>
                                    <td>£{s.amount ? (s.amount / 100).toFixed(2) : '0.00'}</td>
                                    <td>
                                        <span className={`${styles.membershipInfo} ${s.status === 'past_due' ? styles.pastDue : s.overdue ? styles.pastDue : styles.active}`}>
                                            {s.overdue && s.status === 'active' ? 'overdue' : s.status}
                                        </span>
                                    </td>
                                    <td className={styles.billingStatusDate}>{s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString('en-GB') : '—'}</td>
                                    <td className={s.blockedReason ? styles.billingStatusIssue : styles.billingStatusIssueMuted}>{s.blockedReason || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.list}>
            <div className={styles.filterRow}>
                <select
                    value={memberFilter}
                    onChange={(e) => setMemberFilter(e.target.value)}
                    className={styles.memberSelect}
                >
                    <option value="">All Members</option>
                    {members.map(m => (
                        <option key={m.email} value={m.email}>
                            {m.name ? `${m.name} (${m.email})` : m.email}
                        </option>
                    ))}
                </select>
                {memberFilter && (
                    <button className={styles.clearFilter} onClick={() => setMemberFilter('')}>
                        Clear
                    </button>
                )}
            </div>
            <button onClick={exportCsv} className={styles.button}>Export Transactions</button>
            <div className={styles.billingActionRow}>
                <button onClick={runRecurring} className={`btn ${styles.recurringButton}`} disabled={processing}>
                    {processing ? 'Processing...' : 'Run Recurring Payments'}
                </button>
                <button onClick={checkBillingStatus} className={`btn ${styles.billingButton}`} disabled={loadingBillingStatus}>
                    {showBillingStatus ? 'Hide Billing Status' : (loadingBillingStatus ? 'Checking...' : 'Check Billing Status')}
                </button>
            </div>

            {renderProcessResult()}
            {renderBillingStatus()}

            {!filtered.length && <div><p>There aren't any transactions to show.</p></div>}

            {!!filtered.length && (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Member</th>
                                <th>Plan</th>
                                <th>Amount</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Next Payment / Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((t) => (
                                <Item key={t._id} {...t} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default List;
